import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import CustomerHeader from "@/components/CustomerHeader";
import { Send, Clock, Loader2, ArrowLeft, RefreshCw, XCircle, CheckCircle2, ImagePlus, Star } from "lucide-react";
import { toast } from "sonner";

function formatMSS(sec) {
  if (sec <= 0) return "00:00";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

async function fileToBase64(file, maxDim = 1280, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > height && width > maxDim) { height *= maxDim / width; width = maxDim; }
        else if (height > maxDim) { width *= maxDim / height; height = maxDim; }
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function ChatRoom() {
  const { id: cid } = useParams();
  const nav = useNavigate();
  const { user, refreshUser } = useAuth();
  const [consult, setConsult] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [connected, setConnected] = useState(false);
  const [expiresAt, setExpiresAt] = useState(null);
  const [remaining, setRemaining] = useState(0);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [rating, setRating] = useState(5);
  const [review, setReview] = useState("");
  const [previewImg, setPreviewImg] = useState(null);
  const [extPending, setExtPending] = useState(false);
  const [extCost, setExtCost] = useState(0);
  const wsRef = useRef(null);
  const listRef = useRef(null);
  const fileRef = useRef(null);
  const finalizedRef = useRef(false);
  const isFalci = user && consult && user.falci_id === consult.falci_id;
  const isCustomer = user && consult && user.id === consult.user_id;

  const loadConsultation = useCallback(async () => {
    try {
      const { data } = await api.get(`/consultations/${cid}`);
      setConsult(data);
      setMessages(data.messages || []);
      if (data.session_expires_at) setExpiresAt(data.session_expires_at);
      if (data.extension_pending) { setExtPending(true); setExtCost(data.extension_pending_cost || 0); }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Konsültasyon yüklenemedi");
      nav("/panel");
    }
  }, [cid, nav]);

  useEffect(() => { loadConsultation(); }, [loadConsultation]);

  // Timer
  useEffect(() => {
    if (!expiresAt) { setRemaining(0); return; }
    const tick = () => {
      const now = Date.now();
      const exp = new Date(expiresAt).getTime();
      setRemaining(Math.max(0, Math.floor((exp - now) / 1000)));
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [expiresAt]);

  // Customer heartbeat (only user side)
  useEffect(() => {
    if (!isCustomer || !consult || consult.status !== "in_progress") return;
    const ping = () => api.post(`/consultations/${cid}/heartbeat`).catch(() => {});
    ping();
    const t = setInterval(ping, 30000);
    return () => clearInterval(t);
  }, [isCustomer, consult, cid]);

  // Auto-complete on customer window close
  useEffect(() => {
    if (!isCustomer || !consult || !["in_progress", "accepted"].includes(consult.status)) return;
    const onUnload = () => {
      finalizedRef.current = true;
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/consultations/${cid}/complete`;
      const token = localStorage.getItem("falzilla_token");
      const blob = new Blob([JSON.stringify({ review: "Görüşme otomatik sonlandı (tarayıcı kapatıldı)" })], { type: "application/json" });
      // sendBeacon doesn't support custom headers → use fetch with keepalive
      try {
        fetch(url, { method: "POST", body: blob, keepalive: true, headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` } });
      } catch { /* ignore */ }
    };
    window.addEventListener("beforeunload", onUnload);
    return () => window.removeEventListener("beforeunload", onUnload);
  }, [isCustomer, consult, cid]);

  // WebSocket
  useEffect(() => {
    if (!cid || !user) return;
    const token = localStorage.getItem("falzilla_token");
    if (!token) return;
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    const wsUrl = backend.replace(/^http/, "ws") + `/api/ws/consultations/${cid}?token=${encodeURIComponent(token)}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => setConnected(true);
    ws.onclose = () => setConnected(false);
    ws.onerror = () => setConnected(false);
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        if (msg.type === "history") {
          setMessages(msg.messages || []);
          if (msg.session_expires_at) setExpiresAt(msg.session_expires_at);
        } else if (msg.type === "message") {
          setMessages((prev) => [...prev, msg.message]);
        } else if (msg.type === "session_started") {
          setExpiresAt(msg.session_expires_at);
          setConsult((c) => c ? { ...c, status: "in_progress", session_started_at: new Date().toISOString() } : c);
          if (msg.message) setMessages((prev) => [...prev, msg.message]);
          toast.success("Seans başladı!");
        } else if (msg.type === "session_extended") {
          setExpiresAt(msg.session_expires_at);
          setExtPending(false);
          toast.success(`Seans uzatıldı (+${msg.extension_cost} jeton)`);
        } else if (msg.type === "extension_requested") {
          setExtPending(true);
          setExtCost(msg.cost || 0);
          if (isFalci) toast.info(`Kullanıcı ${msg.cost} jeton ile uzatma istiyor`);
        } else if (msg.type === "extension_rejected") {
          setExtPending(false);
          toast.warning(`Uzatma reddedildi: ${msg.reason || ""}`);
        } else if (msg.type === "extension_cancelled") {
          setExtPending(false);
          toast.info("Uzatma isteği iptal edildi");
        } else if (msg.type === "session_completed") {
          setConsult((c) => c ? { ...c, status: "completed", result_text: msg.result_text, duration_seconds: msg.duration_seconds, user_review: msg.user_review, user_rating: msg.user_rating } : c);
          toast.info(msg.auto ? "Görüşme otomatik sonlandı" : "Seans tamamlandı");
        } else if (msg.type === "session_cancelled") {
          setConsult((c) => c ? { ...c, status: "cancelled" } : c);
          toast.error(`Seans iptal edildi: ${msg.reason || ""}`);
        } else if (msg.type === "session_expired") {
          setConsult((c) => c ? { ...c, status: "expired" } : c);
          toast.warning("Süre doldu");
        }
      } catch { /* ignore */ }
    };
    return () => { try { ws.close(); } catch { /* ignore */ } };
  }, [cid, user]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  const send = () => {
    const t = text.trim();
    if (!t || !wsRef.current || wsRef.current.readyState !== 1) return;
    wsRef.current.send(JSON.stringify({ type: "message", text: t }));
    setText("");
  };

  const sendImage = async (file) => {
    if (!file || !wsRef.current || wsRef.current.readyState !== 1) return;
    setUploading(true);
    try {
      const b64 = await fileToBase64(file, 1280, 0.85);
      wsRef.current.send(JSON.stringify({ type: "message", text: text.trim(), image_base64: b64 }));
      setText("");
    } catch { toast.error("Görsel yüklenemedi"); }
    finally { setUploading(false); }
  };

  const rejectConsult = async () => {
    if (!window.confirm("Reddetmek istediğine emin misin? Kullanıcının jetonu iade edilecek.")) return;
    setBusy(true);
    try { await api.post(`/falci/consultations/${cid}/reject`, { reason: "Falcı reddetti" }); toast.info("Reddedildi"); nav("/falci-panel"); }
    catch (e) { toast.error(e.response?.data?.detail || "İşlem başarısız"); }
    finally { setBusy(false); }
  };
  const acceptConsult = async () => {
    setBusy(true);
    try { await api.post(`/falci/consultations/${cid}/accept`); toast.success("Kabul ettin, seans başladı"); }
    catch (e) { toast.error(e.response?.data?.detail || "Kabul edilemedi"); }
    finally { setBusy(false); }
  };
  const extendConsult = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/consultations/${cid}/extend`);
      toast.info(`Uzatma isteğin gönderildi (${data.extension_cost} jeton — falcı onayı bekleniyor)`);
      setExtPending(true);
      setExtCost(data.extension_cost || 0);
    } catch (e) { toast.error(e.response?.data?.detail || "Uzatma isteği başarısız"); }
    finally { setBusy(false); }
  };
  const cancelExtension = async () => {
    setBusy(true);
    try { await api.post(`/consultations/${cid}/extend/cancel`); setExtPending(false); }
    catch (e) { toast.error(e.response?.data?.detail || "İptal başarısız"); }
    finally { setBusy(false); }
  };
  const approveExtension = async () => {
    setBusy(true);
    try { await api.post(`/falci/consultations/${cid}/extend/approve`); toast.success("Uzatma onaylandı"); }
    catch (e) { toast.error(e.response?.data?.detail || "Onay başarısız"); }
    finally { setBusy(false); }
  };
  const rejectExtension = async () => {
    setBusy(true);
    try { await api.post(`/falci/consultations/${cid}/extend/reject`, { reason: "Falcı reddetti" }); toast.info("Uzatma reddedildi"); }
    catch (e) { toast.error(e.response?.data?.detail || "Red başarısız"); }
    finally { setBusy(false); }
  };
  const submitComplete = async () => {
    setBusy(true);
    try {
      await api.post(`/consultations/${cid}/complete`, { rating, review });
      toast.success("Görüşme tamamlandı");
      setShowCompleteModal(false);
      setTimeout(() => nav("/panel"), 800);
    } catch (e) { toast.error(e.response?.data?.detail || "İşlem başarısız"); }
    finally { setBusy(false); }
  };

  if (!consult) return <div className="cosmic-bg min-h-screen flex items-center justify-center"><div className="stars"/><Loader2 className="w-8 h-8 animate-spin gold-text" /></div>;

  const status = consult.status;
  const canChat = status === "in_progress";
  const timerLow = remaining > 0 && remaining < 60;

  return (
    <div className="cosmic-bg min-h-screen flex flex-col">
      <div className="stars" />
      <CustomerHeader />
      <main className="flex-1 max-w-3xl w-full mx-auto px-4 py-6 flex flex-col" data-testid="chat-room">
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => nav(isFalci ? "/falci-panel" : "/panel")} className="flex items-center gap-2 text-white/60 hover:text-white text-sm" data-testid="chat-back">
            <ArrowLeft className="w-4 h-4" /> Geri
          </button>
          <div className="flex items-center gap-3">
            {canChat && (
              <div className={`token-pill px-3 py-1.5 rounded-full text-sm flex items-center gap-1.5 ${timerLow ? "bg-red-500/30" : ""}`} data-testid="session-timer">
                <Clock className="w-3.5 h-3.5" /> {formatMSS(remaining)}
              </div>
            )}
            <div className={`text-xs px-2 py-1 rounded-full ${connected ? "bg-emerald-500/20 text-emerald-200" : "bg-white/10 text-white/50"}`}>
              {connected ? "Bağlı" : "Bağlanılıyor…"}
            </div>
          </div>
        </div>

        <div className="glass rounded-2xl p-4 mb-4 flex items-center justify-between">
          <div>
            <div className="font-serif-myst text-xl">
              {isFalci ? "Müşteri" : (consult.falci_name || "Falcı")}
            </div>
            <div className="text-xs text-white/50 capitalize">{consult.fortune_type} • Durum: {status}</div>
          </div>
          <div className="flex gap-2 flex-wrap justify-end">
            {isFalci && status === "pending" && <>
              <button onClick={acceptConsult} disabled={busy} data-testid="falci-accept" className="btn-gold px-4 py-2 rounded-full text-sm flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> Kabul</button>
              <button onClick={rejectConsult} disabled={busy} data-testid="falci-reject" className="btn-ghost px-4 py-2 rounded-full text-sm flex items-center gap-1"><XCircle className="w-4 h-4" /> Reddet</button>
            </>}
            {isCustomer && canChat && (
              <>
                {!extPending && <button onClick={extendConsult} disabled={busy} data-testid="user-extend" className="btn-ghost px-3 py-2 rounded-full text-xs flex items-center gap-1"><RefreshCw className="w-3.5 h-3.5" /> 15dk uzat</button>}
                <button onClick={() => setShowCompleteModal(true)} disabled={busy} data-testid="user-complete" className="btn-gold px-4 py-2 rounded-full text-sm">Görüşmeyi Bitir</button>
              </>
            )}
          </div>
        </div>

        {/* Extension request banner */}
        {extPending && (
          <div className="glass rounded-2xl p-4 mb-3 border border-amber-400/40 flex items-center justify-between gap-3" data-testid="extension-banner">
            <div className="text-sm">
              {isCustomer
                ? <>Falcı için <span className="gold-text font-semibold">{extCost} jeton</span> ile 15 dk uzatma isteği gönderdin. Onay bekleniyor…</>
                : <>Kullanıcı <span className="gold-text font-semibold">{extCost} jeton</span> ile 15 dk uzatma istiyor.</>}
            </div>
            <div className="flex gap-2 shrink-0">
              {isCustomer && <button onClick={cancelExtension} disabled={busy} data-testid="user-cancel-extend" className="btn-ghost px-3 py-1.5 rounded-full text-xs">Vazgeç</button>}
              {isFalci && <>
                <button onClick={approveExtension} disabled={busy} data-testid="falci-approve-extend" className="btn-gold px-3 py-1.5 rounded-full text-xs">Onayla</button>
                <button onClick={rejectExtension} disabled={busy} data-testid="falci-reject-extend" className="btn-ghost px-3 py-1.5 rounded-full text-xs text-red-300">Reddet</button>
              </>}
            </div>
          </div>
        )}

        {/* Show review to falci when completed */}
        {isFalci && status === "completed" && (consult.user_review || consult.user_rating) && (
          <div className="glass rounded-2xl p-4 mb-3 gold-border" data-testid="falci-review-panel">
            <div className="text-xs text-white/60 mb-1">Müşteri Yorumu</div>
            {consult.user_rating && (
              <div className="flex gap-1 mb-2">{[1,2,3,4,5].map(i => <Star key={i} className={`w-4 h-4 ${i <= consult.user_rating ? "fill-[#D4AF37] text-[#D4AF37]" : "text-white/20"}`} />)}</div>
            )}
            {consult.user_review && <div className="text-sm">"{consult.user_review}"</div>}
            {consult.duration_seconds !== undefined && <div className="text-[10px] text-white/40 mt-2">Süre: {Math.round((consult.duration_seconds || 0) / 60)} dk</div>}
          </div>
        )}

        <div ref={listRef} data-testid="chat-messages" className="flex-1 glass rounded-2xl p-4 mb-3 overflow-y-auto min-h-[380px] max-h-[55vh] space-y-2">
          {messages.length === 0 && <div className="text-center text-white/40 pt-16">Henüz mesaj yok. Seans başladığında sohbet açılır.</div>}
          {messages.map((m) => {
            const mine = m.sender_id === user?.id;
            const system = m.sender_type === "system";
            if (system && (!m.cards || m.cards.length === 0)) return <div key={m.id} className="text-center text-xs text-white/40 py-2">— {m.text} —</div>;
            if (system && m.cards && m.cards.length > 0) {
              return (
                <div key={m.id} className="my-2" data-testid={`cards-msg-${m.id}`}>
                  <div className="text-center text-xs text-white/50 mb-2">🃏 {m.text.replace("🃏 ", "")}</div>
                  <div className="flex flex-wrap justify-center gap-2">
                    {m.cards.map((c, i) => (
                      <div key={i} className="w-20 sm:w-24 text-center">
                        {c.img
                          ? <img src={c.img} alt={c.name} onClick={() => setPreviewImg(c.img)} className="w-full aspect-[2/3] rounded-md object-cover cursor-pointer gold-border" />
                          : <div className="w-full aspect-[2/3] rounded-md bg-gradient-to-br from-[#2b1c4a] to-[#150e2b] flex items-center justify-center gold-border"><span className="gold-text font-bold text-lg">{i + 1}</span></div>}
                        <div className="text-[10px] text-white/70 mt-1">{i + 1}. {c.name}</div>
                        {c.meaning && <div className="text-[9px] text-white/40 italic">{c.meaning}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              );
            }
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[75%] px-3 py-2 rounded-2xl text-sm ${mine ? "bg-[#D4AF37]/25 text-white" : "bg-white/5 text-white"}`} data-testid={`msg-${m.id}`}>
                  {m.image_base64 && <img src={m.image_base64} alt="attachment" onClick={() => setPreviewImg(m.image_base64)} className="rounded-lg mb-1 max-h-48 cursor-pointer" />}
                  {m.text && <div>{m.text}</div>}
                  <div className="text-[9px] text-white/40 mt-0.5">{new Date(m.created_at).toLocaleTimeString()}</div>
                </div>
              </div>
            );
          })}
        </div>

        {canChat ? (
          <div className="flex gap-2 items-center" data-testid="chat-input-row">
            <input ref={fileRef} type="file" accept="image/*" onChange={(e) => e.target.files?.[0] && sendImage(e.target.files[0])} className="hidden" data-testid="chat-file-input" />
            <button onClick={() => fileRef.current?.click()} disabled={uploading || !connected} title="Fotoğraf gönder" data-testid="chat-image-btn" className="btn-ghost w-12 h-12 rounded-full flex items-center justify-center shrink-0">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
            </button>
            <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Mesajını yaz…" data-testid="chat-input" className="flex-1 bg-white/5 border border-white/10 rounded-full px-5 py-3 text-white min-w-0" />
            <button onClick={send} disabled={!connected || !text.trim()} data-testid="chat-send" className="btn-gold w-14 h-12 rounded-full flex items-center justify-center shrink-0"><Send className="w-4 h-4" /></button>
          </div>
        ) : (
          <div className="glass rounded-2xl p-4 text-center text-white/60 text-sm">
            {status === "pending" && (isFalci ? "Kabul veya reddet." : "Falcının kabul etmesi bekleniyor…")}
            {status === "completed" && (
              <div>
                Görüşme tamamlandı.
                {consult.result_text && <div className="mt-2 text-xs text-white/50">"{consult.result_text}"</div>}
                {consult.duration_seconds > 0 && <div className="mt-1 text-[10px] text-white/40">Süre: {Math.round(consult.duration_seconds / 60)} dk</div>}
              </div>
            )}
            {status === "cancelled" && "Seans iptal edildi. Jetonun iade edildi."}
            {status === "expired" && "Süre doldu."}
          </div>
        )}

        {showCompleteModal && (
          <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setShowCompleteModal(false)}>
            <div className="glass-strong rounded-3xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="complete-modal">
              <h2 className="font-serif-myst text-2xl mb-2">Görüşme nasıldı?</h2>
              <p className="text-white/60 text-sm mb-4">Yorumun falcının panelinde görünecek. Erken bitirmek istiyorsan da yorumun önemli.</p>
              <div className="flex gap-1 justify-center mb-4">
                {[1,2,3,4,5].map(i => (
                  <button key={i} onClick={() => setRating(i)} data-testid={`rating-${i}`} className="p-1">
                    <Star className={`w-8 h-8 transition-colors ${i <= rating ? "fill-[#D4AF37] text-[#D4AF37]" : "text-white/20"}`} />
                  </button>
                ))}
              </div>
              <textarea value={review} onChange={(e) => setReview(e.target.value)} rows={3} placeholder="Yorumunu yaz..." data-testid="complete-review" className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-white text-sm" />
              <div className="flex gap-2 mt-4">
                <button onClick={submitComplete} disabled={busy} data-testid="complete-submit" className="btn-gold flex-1 py-2.5 rounded-full">Bitir & Gönder</button>
                <button onClick={() => setShowCompleteModal(false)} className="btn-ghost px-5 rounded-full">Vazgeç</button>
              </div>
            </div>
          </div>
        )}

        {previewImg && (
          <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setPreviewImg(null)}>
            <img src={previewImg} alt="preview" className="max-h-[90vh] max-w-[90vw] rounded-2xl" />
          </div>
        )}
      </main>
    </div>
  );
}
